import React from 'react';

const DAY_NAMES = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

// Thu=4, Fri=5, Sat=6 are weekends in this system
const isWeekend = (date) => [4, 5, 6].includes(date.getDay());

function buildCalendarGrid(start, end) {
  const dates = [];
  const cur  = new Date(start + 'T00:00:00');
  const last = new Date(end   + 'T00:00:00');
  while (cur <= last) { dates.push(new Date(cur)); cur.setDate(cur.getDate() + 1); }

  const weeks = [];
  let week = Array(dates[0].getDay()).fill(null);
  for (const d of dates) {
    week.push(d);
    if (week.length === 7) { weeks.push(week); week = []; }
  }
  if (week.length) { while (week.length < 7) week.push(null); weeks.push(week); }
  return weeks;
}

// ── Role badge ────────────────────────────────────────────────────────────────

function RoleBadge({ role, isSSLCover }) {
  if (isSSLCover) return (
    <span className="rounded bg-purple-100 px-1 py-0.5 text-[10px] font-bold text-purple-700">SSL</span>
  );
  if (role === 'SITTER') return (
    <span className="rounded bg-yellow-100 px-1 py-0.5 text-[10px] font-bold text-yellow-700">S</span>
  );
  return null;
}

// ── Single SL chip in a day cell ─────────────────────────────────────────────

function SLChip({ assignment }) {
  const { sl_detail, role, is_ssl_cover } = assignment;
  const name = sl_detail
    ? `${sl_detail.first_name} ${sl_detail.last_name[0]}.`
    : '—';

  return (
    <div className="flex items-center gap-1 rounded bg-white/70 px-1.5 py-0.5 text-xs font-medium text-gray-800 truncate">
      <span className="truncate">{name}</span>
      <RoleBadge role={role} isSSLCover={is_ssl_cover} />
    </div>
  );
}

// ── Individual day cell ───────────────────────────────────────────────────────

function DayCell({ date, assignments, isHeavy, isManage, onClick }) {
  const weekend   = isWeekend(date);
  const hasShifts = assignments.length > 0;

  let cellBg = 'bg-white border-gray-200';
  if (isHeavy)        cellBg = 'bg-orange-50 border-orange-300';
  else if (weekend)   cellBg = 'bg-blue-50/50 border-blue-200';

  return (
    <div
      className={`rounded-xl border p-2 min-h-[90px] flex flex-col gap-1 ${cellBg} ${
        isManage ? 'cursor-pointer hover:ring-2 hover:ring-indigo-400 transition-all' : ''
      }`}
      onClick={() => isManage && onClick && onClick(date, assignments)}
    >
      {/* Date + type badges */}
      <div className="flex items-center justify-between mb-0.5">
        <span className="text-xs font-bold text-gray-700">{date.getDate()}</span>
        <div className="flex gap-1">
          {isHeavy && (
            <span className="rounded-full bg-orange-400 px-1.5 py-0.5 text-[9px] font-bold text-white">H</span>
          )}
          <span className={`rounded-full px-1.5 py-0.5 text-[9px] font-bold text-white ${weekend ? 'bg-blue-400' : 'bg-gray-400'}`}>
            {weekend ? 'E' : 'D'}
          </span>
        </div>
      </div>

      {/* Assigned SLs */}
      <div className="flex flex-col gap-0.5 flex-1">
        {hasShifts
          ? assignments.map((a) => <SLChip key={a.id} assignment={a} />)
          : <span className="text-[11px] text-gray-300 italic">No assignment</span>
        }
      </div>

      {/* Edit hint for SSL */}
      {isManage && (
        <span className="text-[10px] text-indigo-400 text-right mt-auto">Edit</span>
      )}
    </div>
  );
}

// ── Main calendar ─────────────────────────────────────────────────────────────

export default function ScheduleCalendar({ schedule, isManage = false, onDayClick }) {
  if (!schedule) return null;

  // The list endpoint returns a lighter stub (period as an id) before the
  // detail fetch resolves — render nothing until the full object arrives.
  const period = schedule.period;
  if (!period || typeof period !== 'object' || !period.start_date || !period.end_date) {
    return null;
  }

  const heavySet  = new Set((period.heavy_days ?? []).map((h) => h.date));
  const weeks     = buildCalendarGrid(period.start_date, period.end_date);

  // Group assignments by date string
  const byDate = {};
  for (const a of schedule.assignments ?? []) {
    if (!byDate[a.date]) byDate[a.date] = [];
    byDate[a.date].push(a);
  }

  // Month label (period may span two months)
  const startLabel = new Date(period.start_date + 'T00:00:00')
    .toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
  const endLabel   = new Date(period.end_date   + 'T00:00:00')
    .toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
  const headerLabel = startLabel === endLabel ? startLabel : `${startLabel} — ${endLabel}`;

  return (
    <div className="space-y-3">
      <h3 className="text-base font-semibold text-gray-700">{headerLabel}</h3>

      {/* Legend */}
      <div className="flex flex-wrap gap-3 text-xs text-gray-500">
        {[
          { dot: 'bg-gray-400',   label: 'D = Weekday' },
          { dot: 'bg-blue-400',   label: 'E = Weekend' },
          { dot: 'bg-orange-400', label: 'H = Heavy' },
          { dot: 'bg-yellow-400', label: 'S = Sitter' },
          { dot: 'bg-purple-400', label: 'SSL Cover' },
        ].map(({ dot, label }) => (
          <span key={label} className="flex items-center gap-1">
            <span className={`inline-block w-2.5 h-2.5 rounded-full ${dot}`} /> {label}
          </span>
        ))}
      </div>

      {/* Day-of-week header */}
      <div className="grid grid-cols-7 gap-1.5">
        {DAY_NAMES.map((n) => (
          <div key={n} className="text-center text-xs font-semibold text-gray-400 pb-1">{n}</div>
        ))}

        {weeks.map((week, wi) =>
          week.map((date, di) =>
            date ? (
              <DayCell
                key={date.toISOString()}
                date={date}
                assignments={byDate[date.toISOString().slice(0, 10)] ?? []}
                isHeavy={heavySet.has(date.toISOString().slice(0, 10))}
                isManage={isManage}
                onClick={onDayClick}
              />
            ) : (
              <div key={`empty-${wi}-${di}`} />
            )
          )
        )}
      </div>
    </div>
  );
}
